/**
 * Track replay panel.
 * Allows selecting a UID, loading historical frames, and controlling playback.
 */

import { useState, useEffect, useRef } from 'react';
import { MissionSessions } from './mission-sessions';
import { Play, Loader2 } from 'lucide-react';
import { queryKeys } from '@vtak/shared';
import { useReplayStore } from '@/stores/replay-store';
import { useContactsStore } from '@/stores/contacts-store';
import { apiClient } from '@/services/api-client';
import { queryClient } from '@/queries/query-client';
import { TimelineScrubber } from './replay/timeline-scrubber';
import { ReplayControls } from './replay/replay-controls';
import { REPLAY_TICK_MS } from '@/constants/drawing';
import { useReplayControls } from '@/hooks/use-replay-controls';

/** Replay panel with UID selector, timeline, and playback controls */
export function ReplayPanel(): React.JSX.Element {
  const [uidInput, setUidInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const contacts = useContactsStore((s) => s.contacts);
  const { uid, callsign, frames, currentIndex, playing, speed } = useReplayStore();
  const { loadFrames, tick, reset } = useReplayStore();
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useReplayControls();

  useEffect(() => {
    if (playing) {
      tickRef.current = setInterval(tick, REPLAY_TICK_MS / speed);
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [playing, speed, tick]);

  useEffect(() => () => reset(), [reset]);

  async function handleLoad(): Promise<void> {
    const trimmed = uidInput.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.tak.cotHistory(trimmed),
        queryFn: () => apiClient.getUidHistory(trimmed),
      });
      if (data.length === 0) {
        setError('No history found for this UID.');
        return;
      }
      const contact = contacts.find((c) => c.uid === trimmed);
      loadFrames(trimmed, contact?.callsign ?? trimmed, data);
    } catch {
      setError('Failed to load track history.');
    } finally {
      setLoading(false);
    }
  }

  const currentFrame = frames[currentIndex];
  const hasFrames = frames.length > 0;

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <MissionSessions />
      <div className="space-y-2">
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Unit UID
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value)}
            placeholder="Enter UID or select contact..."
            className="flex-1 rounded bg-white/[0.06] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-echo-yellow"
          />
          <button
            onClick={handleLoad}
            disabled={loading || !uidInput.trim()}
            className="flex items-center gap-1 rounded bg-echo-yellow px-3 py-2 text-xs font-medium text-black disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Load
          </button>
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>

      {contacts.length > 0 && (
        <div className="space-y-1">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Or select contact
          </label>
          <select
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value)}
            className="w-full rounded bg-white/[0.06] px-3 py-2 text-sm text-foreground outline-none"
          >
            <option value="">-- Select --</option>
            {contacts.map((c) => (
              <option key={c.uid} value={c.uid ?? ''}>
                {c.callsign ?? c.uid}
              </option>
            ))}
          </select>
        </div>
      )}

      {hasFrames && (
        <div className="space-y-3 rounded bg-white/[0.04] p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{callsign || uid}</span>
            <span>{frames.length} frames</span>
          </div>

          <TimelineScrubber />
          <ReplayControls />

          {currentFrame && (
            <div className="text-xs text-muted-foreground">
              <p>{currentFrame.timestamp}</p>
              <p>
                {currentFrame.position.lat.toFixed(6)}, {currentFrame.position.lon.toFixed(6)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
